import { formatIsbdTitle } from "@biblioteksentralen/cordata";
import { Button, Heading, Icon, StackProps, Text, VStack } from "@biblioteksentralen/react";
import { MutableRefObject } from "react";
import { ArrowDown } from "react-feather";
import { useTranslation } from "../../../../utils/hooks/useTranslation";
import { getLocalNumberOfPlayersString } from "../../../cordata/interactiveWorks";
import { Collections } from "../../Collections";
import LabelAndValue from "../../LabelAndValue";
import { ListContributors } from "../../ListContributors";
import { TruncatedList } from "../../TruncatedList";
import { getDotseparated } from "../../getDotseparated";
import { useWorkViewContext } from "../WorkViewContextProvider";
import { getAgeGroupLabels } from "../../../cordata/ageGroups";
import { transformGenreName } from "../../../cordata/genres";
import { sift } from "radash";
import { useRediaPlatformContext } from "../../../../rediaPlatform/RediaPlatformProvider";
import { getPath } from "@libry-content/common";

type WorkInfoProps = {
  manifestationsTitleRef: MutableRefObject<HTMLAnchorElement | null>;
};

export const WorkInfo = ({ manifestationsTitleRef, ...stackProps }: WorkInfoProps & StackProps) => {
  const { t, ts } = useTranslation();

  const { work, hasValidHoldings, currentRepresentativeWorkTriple } = useWorkViewContext();
  const { rediaPlatformIsEnabled } = useRediaPlatformContext();

  const workYear = work?.workYear;
  const formGenres = work?.getGenres("form") ?? [];
  const nonFormGenres = work?.getGenres("nonForm") ?? [];
  const genreLabels = sift([...formGenres, ...nonFormGenres].map(transformGenreName(ts)));

  if (!work || !currentRepresentativeWorkTriple) {
    const formattedWorkTitle = work?.formattedTitle;
    const hasLongWorkTitle = (formattedWorkTitle?.length ?? 0) > 100;
    const workTitleFontSize = hasLongWorkTitle ? { base: "2xl", sm: "xl", md: "2xl" } : { base: "2xl", sm: "3xl" };

    return (
      <VStack alignItems="flex-start" justifyContent="space-between" spacing="0" {...stackProps}>
        <VStack alignItems="flex-start" spacing="1">
          <Text lineHeight="1.2">{getDotseparated(genreLabels)}</Text>
          <Heading as="h1" fontSize={workTitleFontSize} fontWeight="semibold">
            {formattedWorkTitle}
          </Heading>
        </VStack>
      </VStack>
    );
  }

  const directors = currentRepresentativeWorkTriple.getContributorsWithRoles(["regissør"]);
  const translators = currentRepresentativeWorkTriple.getContributorsWithRoles(["overs."]);
  const narrators = currentRepresentativeWorkTriple.getContributorsWithRoles(["innl."]);
  const illustrators = currentRepresentativeWorkTriple.getContributorsWithRoles(["illustr.", "kunstner"]);
  const originalAuthor = currentRepresentativeWorkTriple.getContributorsWithRoles(["oppr.forf."]);
  const scriptWriters = currentRepresentativeWorkTriple.getContributorsWithRoles(["manusforf."]);
  const actors = currentRepresentativeWorkTriple.getContributorsWithRoles(["skuesp."]);
  const spokenLanguages = currentRepresentativeWorkTriple.getSpokenLanguagesFormatted({ capitalizeFirstLetter: true });
  const parallelTitles = currentRepresentativeWorkTriple.parallelTitles;
  const ageGroupLabels = getAgeGroupLabels(t, ts, currentRepresentativeWorkTriple.work.ageGroups);
  const genresAndAgeGroups = [...genreLabels, ...ageGroupLabels];
  const mainContributor = currentRepresentativeWorkTriple.mainContributor;
  const formattedTitle = currentRepresentativeWorkTriple.getFormattedTitle();
  const hasLongTitle = (formattedTitle?.length ?? 0) > 100;
  const titleFontSize = hasLongTitle ? { base: "2xl", sm: "xl", md: "2xl" } : { base: "2xl", sm: "3xl" };

  const numberOfPlayers = currentRepresentativeWorkTriple.numberOfPlayers;
  const ageLimit = currentRepresentativeWorkTriple.work.ageLimit;

  return (
    <VStack alignItems="flex-start" justifyContent="space-between" spacing="0" {...stackProps}>
      <VStack alignItems="flex-start" spacing="1">
        <Text lineHeight="1.2">{getDotseparated(genresAndAgeGroups)}</Text>

        <Heading as="h1" fontSize={titleFontSize} fontWeight="semibold">
          {[formattedTitle, ...parallelTitles].join(" | ")}
        </Heading>
        {mainContributor?.agent && (
          <TruncatedList
            items={sift([{ ...mainContributor.agent, href: getPath(mainContributor.agent) }])}
            max={2}
            label="forfatter"
            fontSize="xl"
            _hover={{ textDecoration: "none !important" }}
          />
        )}
        {directors.length > 0 && <ListContributors fontSize="xl" contributors={directors} />}
        {workYear && (
          <LabelAndValue label={t("Først utgitt")} fontSize="md">
            {workYear}
          </LabelAndValue>
        )}
        {work.title.titleType === "uniform_title_music" && (
          <LabelAndValue label={t("Standardtittel")} fontSize="md">
            {formatIsbdTitle(work)}
          </LabelAndValue>
        )}
        {!!currentRepresentativeWorkTriple.originalLanguageTitle && (
          <LabelAndValue label={t("Originaltittel")} fontSize="md">
            {currentRepresentativeWorkTriple.originalLanguageTitle}
          </LabelAndValue>
        )}
        {illustrators.length > 0 && <ListContributors fontSize="md" contributors={illustrators} />}
        {originalAuthor.length > 0 && <ListContributors fontSize="md" contributors={originalAuthor} />}
        {translators.length > 0 && <ListContributors fontSize="md" contributors={translators} />}
        {narrators.length > 0 && <ListContributors fontSize="md" contributors={narrators} />}
        {scriptWriters.length > 0 && <ListContributors fontSize="md" contributors={scriptWriters} />}
        {actors.length > 0 && <ListContributors fontSize="md" contributors={actors} />}
        {!!spokenLanguages && <LabelAndValue label={t("Talespråk")}>{spokenLanguages}</LabelAndValue>}
        {!!ageLimit && (
          <LabelAndValue label={t("Aldersgrense")}>
            {ageLimit.years === 0 ? t("Passer for alle") : `${ageLimit.years} ${t("år")}`}
          </LabelAndValue>
        )}
        {numberOfPlayers?.local && (
          <LabelAndValue label={t("Antall spillere")}>{getLocalNumberOfPlayersString(numberOfPlayers)}</LabelAndValue>
        )}
        {numberOfPlayers?.online && <LabelAndValue label={t("Flerspiller på nett")}>{t("Ja")}</LabelAndValue>}
        <Collections fontSize="md" collections={currentRepresentativeWorkTriple.expressionCollections} />
      </VStack>
      {!rediaPlatformIsEnabled ||
        (hasValidHoldings && (
          <Button
            variant="link"
            fontWeight="normal"
            color="black"
            float="right"
            textDecoration="underline"
            marginTop="1.5rem"
            _hover={{ textDecoration: "none" }}
            onClick={() => manifestationsTitleRef.current?.focus()}
            rightIcon={<Icon as={ArrowDown} size="1em" transform="translateX(-20%)" />}
          >
            {t("Se alle utgaver")}
          </Button>
        ))}
    </VStack>
  );
};
